import React from 'react';
import { Helmet } from 'react-helmet';
import styled from 'styled-components';
import {
  Type,
  Image,
  Tooltip,
  FormGroup,
  FormToggle,
  IconUserAltCircle,
  IconCheckAlt,
  IconExclamationAlt,
} from '@spotify-internal/encore-creator-web';
import { success, failure } from '@spotify-internal/encore-foundation';

import { Me } from '../../types';
import * as api from '../../lib/Api/subscription';
import { useMe } from '../../hooks/useMe';
import { FormattedMessage, defineMessages, useIntl } from 'react-intl';

interface Props {
  me: Me;
}

export function Settings(props: Props) {
  const [isSubscribedToMarketingEmails, setIsSubscribedToMarketingEmails] = React.useState(false);
  const [preferencesHaveBeenChanged, setPreferencesHaveBeenChanged] = React.useState<
    boolean | null
  >(null);
  const [showErrorMessage, setshowErrorMessage] = React.useState(false);

  const timeoutRef = React.useRef<number | undefined>();

  React.useEffect(() => {
    let didCancel = false;

    async function fetchData() {
      /* istanbul ignore next */
      if (didCancel) {
        return;
      }
      setIsSubscribedToMarketingEmails(await api.isSubscribedToMarketingEmails());
    }
    fetchData();

    return /* istanbul ignore next */ () => {
      didCancel = true;
    };
  }, []);

  async function handleEmailSubscriptionToggle() {
    clearTimeout(timeoutRef.current);
    const newEmailPreference = !isSubscribedToMarketingEmails;
    try {
      setPreferencesHaveBeenChanged(true);
      setIsSubscribedToMarketingEmails(newEmailPreference);
      await api.updateEmailSubscriptionPreference({
        isSubscribed: newEmailPreference,
      });

      timeoutRef.current = window.setTimeout(
        /*istanbul ignore next */
        () => setPreferencesHaveBeenChanged(false),
        2000,
      );
    } catch (e) {
      setIsSubscribedToMarketingEmails(!newEmailPreference);
      setshowErrorMessage(true);
    }
  }

  return (
    <div data-testid="user-settings-page">
      <Helmet title="User settings" />
      <Type as="h1" variant={Type.heading1}>
        <FormattedMessage
          id="user-settings.title"
          description="Title of page where users can update their settings"
          defaultMessage="User settings"
        />
      </Type>
      <Type as="h3" variant={Type.heading3}>
        <FormattedMessage
          id="user-settings.account-info"
          description="Title of section showing the users account information"
          defaultMessage="Account information"
        />
      </Type>
      <Type as="p" variant={Type.body2} weight={Type.bold}>
        <FormattedMessage
          id="user-settings.logged-in-as"
          description="Header before panel showing who the user is logged in as"
          defaultMessage="Logged in as"
        />
      </Type>
      <UserAccountContainer>
        <UserAccountDetailsContainer>
          {/* istanbul ignore next */ props.me.avatarUrl && props.me.avatarUrl.length > 0 ? (
            <Image
              src={props.me.avatarUrl}
              alt={props.me.displayName}
              imageHeight="50px"
              imageWidth="50px"
              crop
              circle
            />
          ) : (
            <IconUserAltCircle iconSize={48} />
          )}
          <AccountInfoText>
            <Type as="p" variant={Type.body2} weight={Type.bold} style={{ paddingBottom: 0 }}>
              {props.me.displayName}
            </Type>
            <Type as="p" variant={Type.body2} style={{ paddingBottom: 0 }}>
              {props.me.emailAddress}
            </Type>
          </AccountInfoText>
        </UserAccountDetailsContainer>
        <a
          href="https://www.spotify.com/account/overview/"
          title="Edit"
          target="_blank"
          rel="noopener noreferrer"
        >
          <FormattedMessage
            id="user-settings.edit"
            description="Edit the users account information"
            defaultMessage="Edit"
          />
        </a>
      </UserAccountContainer>
      <hr />
      <Type as="h3" variant={Type.heading3} style={{ marginTop: '32px' }}>
        <FormattedMessage
          id="user-settings.email-preferences"
          description="Title for updating email delivery preferences"
          defaultMessage="Email preferences"
        />
      </Type>
      <FormGroup>
        <FormToggle
          checked={isSubscribedToMarketingEmails}
          onChange={handleEmailSubscriptionToggle}
          data-testid="user-settings-email-toggle"
        >
          <b>
            <FormattedMessage
              id="user-settings.spotify-for-podcasters"
              description="The name of this site"
              defaultMessage="Spotify for Podcasters"
            />
          </b>
        </FormToggle>
      </FormGroup>
      <Type as="p" variant={Type.body2}>
        <FormattedMessage
          id="user-settings.marketing-email-description"
          description="Tells th user the benefits of subscribing to marking emails"
          defaultMessage="Updates and important information regarding your content on Spotify for Podcasters. {linebreak}
        If unsubscribed, you may still receive important emails about your Spotify account."
          values={{
            linebreak: <br />,
          }}
        />
      </Type>
      {!showErrorMessage && preferencesHaveBeenChanged && (
        <SuccessComponent didSubscribe={isSubscribedToMarketingEmails} />
      )}
      {showErrorMessage && <ErrorComponent />}
    </div>
  );
}

function SuccessComponent({ didSubscribe }: { didSubscribe: boolean }) {
  const intl = useIntl();

  const msg = defineMessages({
    subscribed: {
      id: 'user-settings.subscription-success',
      defaultMessage: 'You are now subscribed to Spotify for Podcasters',
    },
    unbsubscribed: {
      id: 'user-settings.unsubscription-success',
      defaultMessage: 'You have been unsubscribed from Spotify for Podcasters',
    },
  });

  const message = didSubscribe
    ? intl.formatMessage(msg.subscribed)
    : intl.formatMessage(msg.unbsubscribed);
  return (
    <SucccessText>
      <IconCheckAlt size={16} color={success} style={{ marginRight: '8px' }} /> {message}
    </SucccessText>
  );
}

function ErrorComponent() {
  return (
    <ErrorText data-testid="update-error">
      <IconExclamationAlt size={16} color={failure} />{' '}
      <FormattedMessage
        id="user-settings.error"
        defaultMessage="Something went wrong, please try again."
      />
    </ErrorText>
  );
}

const ErrorText = styled(Type).attrs({
  forwardedAs: 'p',
  variant: Type.body2,
  color: failure,
})`
  display: flex;
  align-items: center;
`;

const SucccessText = styled(Type).attrs({
  forwardedAs: 'p',
  variant: Type.body2,
  color: success,
})`
  display: flex;
  align-items: center;
`;

const UserAccountContainer = styled(Tooltip)`
  display: flex;
  justify-content: space-between;
  width: auto;
  min-width: 400px;
  align-items: center;
  padding-right: 12px;
  margin-bottom: 48px;
`;

const UserAccountDetailsContainer = styled.div`
  display: flex;
  align-items: center;
`;

const AccountInfoText = styled.div`
  padding: 12px;
  display: flex;
  flex-direction: column;
`;
